import { Box, Divider, LinearProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { ServiceFeedbackResponse } from "../../api/types/serviceFeedback.types";
import { StarEmptyIcon, StarFilledIcon, StarReviewsTitleIcon } from "../../assets/icons/go-hire-icons";

import { ratingDetailsStyles } from "./styles";
import RatingDetailsLoader from "./RatingDetailsLoader";

const RatingDetails = ({ meta, isLoading }: { meta?: ServiceFeedbackResponse["meta"]; isLoading?: boolean }) => {
  const { t } = useTranslation("profiles", {
    keyPrefix: "allReviews",
  });

  if (isLoading || !meta) {
    return <RatingDetailsLoader />;
  }

  const reviews = [
    {
      count: 5,
      percentage: meta?.fiveStarCount,
    },
    {
      count: 4,
      percentage: meta?.fourStarCount,
    },
    {
      count: 3,
      percentage: meta?.threeStarCount,
    },
    {
      count: 2,
      percentage: meta?.twoStarCount,
    },
    {
      count: 1,
      percentage: meta?.oneStarCount,
    },
  ];

  const totalReviewsCount =
    meta?.fiveStarCount + meta?.fourStarCount + meta?.threeStarCount + meta?.twoStarCount + meta?.oneStarCount;

  return (
    <Box sx={ratingDetailsStyles.container}>
      <Typography sx={ratingDetailsStyles.titleText}>
        <StarReviewsTitleIcon />
        {t("reviewsCount", { reviewsAverage: Number(meta?.averageRating).toFixed(2), reviewsCount: meta?.totalItems })}
      </Typography>
      <Divider sx={ratingDetailsStyles.divider} />
      {reviews.map((review) => (
        <ReviewMeter
          key={review.count}
          count={review.count}
          ratio={((review.percentage / totalReviewsCount) * 100).toFixed(1)}
        />
      ))}
    </Box>
  );
};

export default RatingDetails;

const ReviewMeter = ({ count, ratio }: { count: number; ratio: string }) => {
  const { t } = useTranslation("profiles", {
    keyPrefix: "allReviews",
  });

  return (
    <Box sx={ratingDetailsStyles.reviewMeterContainer}>
      <Box sx={ratingDetailsStyles.starsContainer}>
        <Box sx={ratingDetailsStyles.starIcons}>
          {Array.from({ length: count }).map((_, index) => (
            <StarFilledIcon key={index} />
          ))}
          {Array.from({ length: 5 - count }).map((_, index) => (
            <StarEmptyIcon key={index} />
          ))}
        </Box>

        <Typography sx={ratingDetailsStyles.starText}>
          {count} {t("star")}
        </Typography>
      </Box>

      <Box sx={ratingDetailsStyles.progressContainer}>
        <LinearProgress
          sx={ratingDetailsStyles.linearProgress}
          variant="determinate"
          value={Number(ratio) > 1 ? Number(ratio) : 1}
        />
        <Typography sx={ratingDetailsStyles.percentageText}>{Number(ratio) > 1 ? `${ratio}%` : "< 1%"}</Typography>
      </Box>
    </Box>
  );
};
